import React from 'react';
import DataManager from '@americanexpress/data-manager';
import { AmexDDLRegister, getAmexDataManagerConfig } from '@americanexpress/data-manager-utils';
import { OneExperimentsProvider, experimentsReducer, prefetchExperiments } from '@americanexpress/one-experiments';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { IntlProvider } from 'react-intl';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import SharedScript, { Adobe } from '@americanexpress/shared-scripts';
import { combineReducers } from 'redux-immutable';
import { OneFetchyeProvider, OneCache } from 'fetchye-one-app';
import childRoutes from '../childRoutes';

import { GlobalContextProvider } from '../containers/GlobalContext';
import ErrorMessage from './ErrorMessage';

const OneCommercialPaymentsEnrollmentRoot = ({ languageData, locale, children }) => {
  const internetIdentityBaseUrls = useSelector((state) => state.getIn(['config', 'internetIdentityBaseUrls']));
  const vpayTenancyUrl = useSelector((state) => state.getIn(['config', 'vpayTenancyUrl']));
  const setTarget = () => {
    if (global.BROWSER && window.AmexSession) {
      let queryParams;
      if (sessionStorage.reqParams) {
        const reqParams = JSON.parse(sessionStorage.reqParams);
        queryParams = new URLSearchParams(reqParams).toString();
      }
      const targetURI = encodeURIComponent(`${vpayTenancyUrl}/card-selector?${queryParams}`);
      const target = encodeURIComponent(`${internetIdentityBaseUrls}/${locale}/account/light/login/?target=${targetURI}`);
      window.AmexSession.options({
        locale,
        target,
      });
    }
  };
  /* istanbul ignore next */
  if (Object.entries(languageData).length > 0) {
    return (
      <IntlProvider locale={locale} messages={languageData}>
        <Helmet
          link={[
            {
              rel: 'stylesheet',
              href: 'https://www.aexp-static.com/cdaas/one/statics/axp-dls/6.18.1/package/dist/6.18.1/styles/dls.min.css',
            },
            { rel: 'icon', href: 'https://www.aexp-static.com/cdaas/one/statics/@americanexpress/static-assets/2.24.2/package/dist/img/logos/dls-logo-bluebox-alt.svg' },
          ]}
        >
          <title>American Express - Account Enrollment</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <body className="dls-gray-02-bg" />
        </Helmet>
        <DataManager
            // eslint-disable-next-line react/jsx-props-no-spreading -- x
          {...getAmexDataManagerConfig({
            initialDDL: {
              historyLimit: 10,
              ddl: {
                version: '1.7.0',
                timestamp: new Date().toISOString(),
                user: { authenticated: true },
              },
            },
          })}
        >
          <AmexDDLRegister />
          <Adobe async={true} version="1.0.0" />
          <SharedScript
            name="one-identity-session"
            version="^1.0.0"
            windowCheck="AmexSession"
            onload={setTarget}
          />
          <OneExperimentsProvider>
            <OneFetchyeProvider>
              <GlobalContextProvider>
                {children}
              </GlobalContextProvider>
            </OneFetchyeProvider>
          </OneExperimentsProvider>
        </DataManager>
      </IntlProvider>
    );
  }
  /* istanbul ignore next */
  return (
    <IntlProvider locale={locale} messages={languageData}>
      <ErrorMessage errorId="genericErrorMessage" />
    </IntlProvider>
  );
};

export const TestableOneCommercialPaymentsEnrollmentRoot = OneCommercialPaymentsEnrollmentRoot;

OneCommercialPaymentsEnrollmentRoot.propTypes = {
  languageData: PropTypes.shape({}).isRequired, // no need to restate all the keys in the lang pack
  locale: PropTypes.string.isRequired,
  children: PropTypes.node,
};

// Read about childRoutes:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/Routing.md#childroutes
OneCommercialPaymentsEnrollmentRoot.childRoutes = childRoutes;

// Read about appConfig:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/App-Configuration.md
/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line n/global-require -- require needs to be inside browser check
  OneCommercialPaymentsEnrollmentRoot.appConfig = require('../appConfig').default;
}

export const loadModuleData = ({ store: { dispatch } }) => dispatch(loadLanguagePack('one-commercial-payments-enrollment-root', { fallbackLocale: 'en-US' }), prefetchExperiments());

OneCommercialPaymentsEnrollmentRoot.holocron = {
  name: 'one-commercial-payments-enrollment-root',
  loadModuleData,
  reducer: combineReducers({
    fetchye: OneCache().reducer,
    experiments: experimentsReducer,
  }),
};

export default oneAppModuleWrapper('one-commercial-payments-enrollment-root')(OneCommercialPaymentsEnrollmentRoot);
